/**
 * usePageTracking Hook
 *
 * Initializes GA4 once consent is granted and sends a page_view on every
 * client-side route change. No-ops entirely when VITE_GA_MEASUREMENT_ID is
 * unset or the visitor hasn't opted in.
 */

import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

import { applyConsentToAnalytics, initAnalytics, trackPageView } from '@/lib/analytics'

export function usePageTracking(consented: boolean): void {
  const location = useLocation()

  useEffect(() => {
    // Keeps GA's opt-out flag in sync, so withdrawing consent mid-session
    // stops an already-loaded gtag.js instead of waiting for a reload.
    applyConsentToAnalytics(consented)

    if (consented) {
      initAnalytics()
    }
  }, [consented])

  useEffect(() => {
    if (!consented) return

    // Let the route's document title settle (react-helmet-async updates it in
    // an effect) before reading it for the page_view.
    const id = window.setTimeout(() => {
      trackPageView(location.pathname + location.search)
    }, 0)

    return () => window.clearTimeout(id)
  }, [consented, location.pathname, location.search])
}
